import { Component, Input, ChangeDetectionStrategy, EventEmitter, Output, input, computed, signal, effect } from '@angular/core';
import { AttributeListRowModel } from '../models/attribute-list-row.model';
import { AttributeListColumnModel } from '../models/attribute-list-column.model';
import { AttributeType } from '@tailormap-viewer/api';
import { AttributeFilterModel } from '@tailormap-viewer/api';
import { FeatureDetailsModel, StatisticType } from '../models/attribute-list-api-service.model';
import { StatisticsHelper } from '../helpers/statistics-helper';
import { AttributeListStatisticColumnModel, StatisticValueModel } from '../models/attribute-list-statistic-column.model';
import { AttributeListPagingDataType } from '../models/attribute-list-paging-data.type';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import {
  MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell,
  MatCellDef, MatCell, MatFooterCellDef, MatFooterCell, MatHeaderRowDef,
  MatHeaderRow, MatRowDef, MatRow, MatFooterRowDef, MatFooterRow,
} from '@angular/material/table';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIconButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AttributeListFeatureDetailsComponent } from '../attribute-list-feature-details/attribute-list-feature-details.component';
import { NgClass } from '@angular/common';
import { TooltipDirective, PanelResizerComponent } from '@tailormap-viewer/shared';

const DEFAULT_COLUMN_WIDTH = 170;

@Component({
  selector: 'tm-attribute-list-table',
  templateUrl: './attribute-list-table.component.html',
  styleUrls: ['./attribute-list-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatIconButton,
    TooltipDirective,
    PanelResizerComponent,
    MatMenuTrigger,
    MatProgressSpinner,
    AttributeListFeatureDetailsComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    MatFooterRowDef,
    MatFooterRow,
  ],
})
export class AttributeListTableComponent {

  public rows = input<AttributeListRowModel[] | null>(null);
  public columns = input<AttributeListColumnModel[] | null>([]);

  @Input()
  public sort: { column: string; direction: string } | null = null;

  @Input()
  public set filters(filters: AttributeFilterModel[] | null) {
    if (filters === null) {
      this.filtersDictionary = new Map();
      return;
    }
    this.filtersDictionary = new Map<string, AttributeFilterModel>(filters.map(f => [ f.attribute, f ]));
  }

  @Input()
  public selectedRowId: string | undefined | null;

  public canExpandRows = input<boolean | null>(false);
  public canCheckRows = input<boolean | null>(false);
  public checkedRows = input<string[] | null>(null);
  public featureDetails = input<Map<string, FeatureDetailsModel> | null>(new Map());
  public loadingFeatureDetailsIds = input<Set<string> | null>(new Set());

  public showStatistics = input<boolean | null>(false);
  public statistics = input<AttributeListStatisticColumnModel[] | null>([]);
  public pagingDataSelectedTab = input<AttributeListPagingDataType | null>(null);

  @Output()
  public selectRow = new EventEmitter<{ id: string; selected: boolean }>();

  @Output()
  public setSort = new EventEmitter<{ columnId: string; direction: 'asc' | 'desc' | '' }>();

  @Output()
  public setFilter = new EventEmitter<{ columnId: string; attributeType: AttributeType }>();

  @Output()
  public loadFeatureDetailsForFeature = new EventEmitter<string>();

  @Output()
  public showStatisticsHelp = new EventEmitter<void>();

  @Output()
  public loadStatisticsForColumn = new EventEmitter<{ type: StatisticType; columnName: string; dataType: string }>();

  @Output()
  public setAllRowsChecked = new EventEmitter<{ checked: boolean }>();

  @Output()
  public setRowChecked = new EventEmitter<{ id: string; checked: boolean }>();

  public columnNames = computed(() => {
    return this.getColumnNames(this.columns(), this.canExpandRows(), this.canCheckRows());
  });
  private filtersDictionary: Map<string, AttributeFilterModel> = new Map();

  private columnWidths: Map<string, number> = new Map();
  private isResizing = false;

  public expandedRows = signal<Set<string>>(new Set());

  public readonly EXPAND_DETAILS_COLUMN_NAME = '__tm_attribute_list_expand_details__';
  public readonly EXPAND_DETAILS_ROW_NAME = '__tm_attribute_list_expand_details_row__';
  public readonly CHECK_ROW_COLUMN_NAME = '__tm_attribute_list_check_rows__';

  public statisticTypes = StatisticsHelper.getStatisticOptions();
  private statisticsDictionary = computed<Map<string, StatisticValueModel>>(() => {
    const statistics = this.statistics() || [];
    return new Map<string, StatisticValueModel>(statistics.map(
      s => {
        const label = s.hasError
          ? $localize`:@@core.attribute-list.statistics-error:Error loading statistic`
          : StatisticsHelper.getLabelForStatisticType(s);
        const value: StatisticValueModel = {
          ...s,
          label,
        };
        return [ s.columnName, value ];
      },
    ));
  });

  public rowIndexMap = computed(() => {
    const paging = this.pagingDataSelectedTab();
    const pageOffset = paging ? (paging.pageIndex - 1) * paging.pageSize : 0;
    const rowIndexMap = new Map<string, number>();
    (this.rows() ?? []).forEach((row, index) => {
      rowIndexMap.set(row.id, pageOffset + index + 1);
    });
    return rowIndexMap;
  });

  public checkedRowsSet = computed(() => new Set((this.checkedRows() || [])));
  public allRowsChecked = computed(() => {
    const checkedRows = this.checkedRowsSet();
    const rows = (this.rows() ?? []);
    return rows.length > 0 && rows.every(row => checkedRows.has(row.id));
  });
  public someRowsChecked = computed(() => {
    const checkedRows = this.checkedRowsSet();
    const rows = (this.rows() ?? []);
    return !this.allRowsChecked() && rows.length > 0 && rows.some(row => checkedRows.has(row.id));
  });

  constructor() {
    effect(() => {
      // reset column names and expanded rows when columns change
      this.columns();
      this.expandedRows.set(new Set());
    });
  }

  public trackByRowId(idx: number, row: AttributeListRowModel) {
    return row.id;
  }

  public trackByColumnId(idx: number, column: AttributeListColumnModel) {
    return column.id;
  }

  private getColumnNames(columns: AttributeListColumnModel[] | null, canExpandRows?: boolean | null, canCheckRows?: boolean | null): string[] {
    if (!columns) {
      return [];
    }
    const names = columns.map(c => c.label || c.id);
    if (canExpandRows && names.length > 0) {
      names.unshift(this.EXPAND_DETAILS_COLUMN_NAME);
    }
    if (canCheckRows && names.length > 0) {
      names.unshift(this.CHECK_ROW_COLUMN_NAME);
    }
    return names;
  }

  public columnWidthChanged(widthDelta: number, col: AttributeListColumnModel) {
    const curWidth = this.columnWidths.get(col.id) || DEFAULT_COLUMN_WIDTH;
    const MIN_WIDTH = 80;
    const MAX_WIDTH = 500;
    this.columnWidths.set(col.id, Math.min(Math.max(MIN_WIDTH, (curWidth + widthDelta)), MAX_WIDTH));
    setTimeout(() => this.isResizing = false, 250);
  }

  public stopEvent($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.isResizing = true;
  }

  public getColumnWidth(col: AttributeListColumnModel) {
    return `${this.columnWidths.get(col.id) || DEFAULT_COLUMN_WIDTH}px`;
  }

  public onRowClick($event: MouseEvent, row: AttributeListRowModel): void {
    $event.stopPropagation();
    this.selectRow.emit({ id: row.id, selected: !this.isSelected(row) });
  }

  public onRowKeydown($event: Event, row: AttributeListRowModel): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.selectRow.emit({ id: row.id, selected: !this.isSelected(row) });
  }

  public onSortClick(columnId: string): void {
    if (this.isResizing) {
      return;
    }
    let direction: 'asc' | 'desc' | '' = 'asc';
    if (this.sort && this.sort.column === columnId) {
      direction = this.sort.direction === 'asc' ? 'desc' : '';
    }
    this.setSort.emit({ columnId, direction });
  }

  public onFilterClick($event: MouseEvent, column: AttributeListColumnModel): void {
    $event.stopPropagation();
    this.setFilter.emit({ columnId: column.id, attributeType: column.type });
  }

  public hasFilter(columnId: string) {
    return this.filtersDictionary.has(columnId);
  }

  public hasDisabledFilter(columnId: string) {
    return this.filtersDictionary.get(columnId)?.disabled;
  }

  public isSelected(row: AttributeListRowModel) {
    return this.selectedRowId === row.id;
  }

  protected onRowExpandClick($event: PointerEvent, row: AttributeListRowModel) {
    $event.stopPropagation();
    const rowId = row.id;
    const featureId = row.__fid;
    if (!featureId) {
      return;
    }
    const expandedRows = new Set(this.expandedRows());
    if (expandedRows.has(rowId)) {
      expandedRows.delete(rowId);
    } else {
      expandedRows.add(rowId);
      this.loadFeatureDetailsForFeature.emit(featureId);
    }
    this.expandedRows.set(expandedRows);
  }

  public getStatisticResult(col: AttributeListColumnModel): StatisticValueModel | undefined {
    return this.statisticsDictionary().get(col.id);
  }

  public isStatisticsProcessing(colName: string): boolean {
    return this.statisticsDictionary().get(colName)?.isLoading ?? false;
  }

  public isStatisticsTypeAvailable(type: StatisticType, col: AttributeListColumnModel) {
    return StatisticsHelper.isStatisticTypeAvailable(type, col.type);
  }

  public isStatisticsTypeSelected(type: StatisticType, col: AttributeListColumnModel) {
    const statisticColumn = this.statisticsDictionary().get(col.id);
    return !!statisticColumn && statisticColumn.type === type;
  }

  public loadStatistic(type: StatisticType, col: AttributeListColumnModel) {
    this.loadStatisticsForColumn.emit({ type, columnName: col.id, dataType: col.type });
  }

  public getAriaSortValue(columnId: string): 'ascending' | 'descending' | 'none' {
    if (!this.sort || this.sort.column !== columnId || this.sort.direction === '') {
      return 'none';
    }
    return this.sort.direction === 'asc' ? 'ascending' : 'descending';
  }

  public getColumnFilterLabel(columnLabel: string): string {
    return $localize`:@@core.attribute-list.filter-column:Filter on ${columnLabel}`;
  }

  public getColumnSortLabel(columnLabel: string): string {
    return $localize`:@@core.attribute-list.sort-column:Sort by ${columnLabel}`;
  }

  public toggleRowChecked(row: AttributeListRowModel) {
    const curChecked = this.checkedRowsSet().has(row.id);
    this.setRowChecked.emit({ id: row.id, checked: !curChecked });
  }

  public toggleAllRowsChecked() {
    const allChecked = this.allRowsChecked();
    this.setAllRowsChecked.emit({ checked: !allChecked });
  }

  public getColumnResizeLabel(columnLabel: string): string {
    return $localize`:@@core.attribute-list.resize-column:Resize ${columnLabel} column`;
  }

}
